import React from 'react'

import { ModelBaseModel } from '../../../models/model-base/model-base.model'
import BillsHeader from './common/BillsHeader'
import { formatDateWithTime } from '../../../helpers/time'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../types/payment'
import { IRegistrationTicketInterface } from '../../../models/registration-tickets/registrationTicket.interface'
import { getFloatPrice } from '../../../helpers/price'

// Types

type Props = {
  data: any
  modalThemeColor: `#${string}`
  showRefund: boolean
  refundgovernanceTotalState: number
  currency: string
}

type ITotalRow = {
  numbersOfUnit: string
  price: string
  modalThemeColor: `#${string}`
  scratched: number
  refunded: number
  showRefund: boolean
  refundgovernanceTotalState: number
  currency: string
}

type IEventReviewPublishInput = {
  className?: string
  data?: any
  price?: any
  onClick?: any
  moreBtn?: boolean
  icon?: string
  postIcon?: string
  style?: React.CSSProperties
}

const EventReviewPublishInput: React.FC<IEventReviewPublishInput> = (args) => {
  const { className, data, price, onClick, moreBtn, icon, postIcon, style = {} } = args

  return (
    <>
      {onClick ? (
        <div
          onClick={onClick}
          style={style}
          className={`${className} text-SeabiscuitDark200ThemeColor`}
        >
          <div className="text-SeabiscuitDark200ThemeColor mr-1 font-normal">{price}</div>
          <div>{data}</div>
          {moreBtn ? <span className="cursor-pointer underline">more</span> : null}
        </div>
      ) : (
        <div className={`${className} text-SeabiscuitDark200ThemeColor`} style={style}>
          {icon ? <img className="w-5 h-5 absolute left-3" src={icon} alt="icon" /> : null}
          <div className="text-SeabiscuitDark200ThemeColor mr-1 font-normal">{price}</div>
          <div>{data}</div>
          {postIcon ? <img className="w-5 h-5 absolute right-3" src={postIcon} alt="icon" /> : null}
          {moreBtn ? <span className="cursor-pointer underline">more</span> : null}
        </div>
      )}
    </>
  )
}

const DetailRow = ({
  item,
  date,
  showRefund,
  currency,
}: {
  item: IRegistrationTicketInterface
  date: string
  showRefund: boolean
  currency: string
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-start w-full">
      <EventReviewPublishInput
        className={`rounded-lg text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'}`}
        data={item.ticketTitle}
      />
      <EventReviewPublishInput
        className={`rounded-lg text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'}`}
        data={date}
      />
      <EventReviewPublishInput
        className={`rounded-lg text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'}`}
        data={item.selectedUnitsCount}
      />
      <EventReviewPublishInput
        className={`rounded-lg text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full ${!showRefund ? 'md:w-4/12' : ' md:w-3/12'}`}
        data={getFloatPrice(`${currency}${item.ticketPrice}`)}
        icon={
          item.paymentType === PAYMENT_TYPE.CARD
            ? '/assets/img/dark/credit-card.svg'
            : item.paymentType === PAYMENT_TYPE.CHECK
              ? '/assets/img/dark/check.svg'
              : '/assets/img/dark/cash.svg'
        }
        postIcon={
          item.paymentStatus === PAYMENT_STATUS.PAID
            ? '/assets/cp_icons/Ok-3.svg'
            : '/assets/cp_icons/Cancel-2.svg'
        }
      />
      {showRefund && (
        <>
          {' '}
          <EventReviewPublishInput
            className="rounded-lg text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full md:w-3/12"
            data={`${item.amountScratched > 0 ? 'Scratched' : 'No'}`}
          />
          <EventReviewPublishInput
            className="rounded-lg text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor relative flex items-center justify-center text-sm p-3 m-1 w-full md:w-3/12"
            data={getFloatPrice(`${currency}${item.amountRefunded}`)}
          />
        </>
      )}
    </div>
  )
}

const TotalRow = (args: ITotalRow) => {
  const {
    numbersOfUnit,
    price,
    modalThemeColor,
    scratched,
    showRefund,
    refundgovernanceTotalState,
    currency,
  } = args

  return (
    <>
      {showRefund ? (
        <>
          <div className="flex flex-col md:flex-row md:items-start w-full">
            <EventReviewPublishInput
              className={
                'rounded-lg text-[#122B46] text-sm p-3 m-1 w-full md:w-[21%] font-semibold bg-SeabiscuitGreenLightThemeColor'
              }
              data={`Total`}
            />
            <EventReviewPublishInput
              className="rounded-lg text-[#122B46] text-sm p-3 m-1 w-full md:w-10/12 font-semibold flex md:justify-end bg-SeabiscuitGreenLightThemeColor"
              price={`${getFloatPrice(`${currency}${price}`)} paid, `}
              data={`${getFloatPrice(`${currency}${refundgovernanceTotalState}`)} refunded`}
            />
          </div>
        </>
      ) : (
        <div className="flex flex-col md:flex-row md:items-start w-full">
          <EventReviewPublishInput
            className={`rounded-lg text-sm p-3 m-1 ml-0 ${!showRefund ? 'w-4/12' : ' w-3/12'} font-semibold`}
            data={`Total`}
            style={{
              background: modalThemeColor,
            }}
          />
          <EventReviewPublishInput
            className={`rounded-lg relative flex items-center justify-center text-sm p-3 m-1 ${!showRefund ? 'w-4/12' : ' w-3/12'} font-semibold`}
            data={`${numbersOfUnit} tickets`}
            style={{
              background: modalThemeColor,
            }}
          />
          <EventReviewPublishInput
            className={`rounded-lg relative flex items-center justify-center text-sm p-3 m-1 ${!showRefund ? 'w-4/12' : ' w-3/12'} font-semibold`}
            data={getFloatPrice(`${currency}${price}`)}
            style={{
              background: modalThemeColor,
            }}
          />
          {showRefund && (
            <>
              {' '}
              <EventReviewPublishInput
                className="rounded-lg relative flex items-center justify-center text-sm p-3 m-1 w-3/12 font-semibold"
                data={`${scratched} tickets`}
                style={{
                  background: modalThemeColor,
                }}
              />
              <EventReviewPublishInput
                className="rounded-lg relative flex items-center justify-center text-sm p-3 m-1 w-3/12 font-semibold"
                data={getFloatPrice(`${currency}${scratched}`)}
                style={{
                  background: modalThemeColor,
                }}
              />
            </>
          )}
        </div>
      )}
    </>
  )
}

const BillDetailsTicketsData = (props: Props) => {
  let ticketTotal = 0
  const { showRefund, refundgovernanceTotalState, currency } = props

  return (
    <>
      {props?.data && props?.data?.length ? (
        <div className="flex items-center">
          <BillsHeader title="Item" tooltipText="Line item" />
          <BillsHeader title="Date" tooltipText="Payed Date" />
          <BillsHeader title="Units" tooltipText="Number of units" />
          <BillsHeader title="Price per unit" tooltipText="Price per unit" />
          {showRefund && (
            <>
              <BillsHeader title="Scratched" tooltipText="Scratched" />
              <BillsHeader title="Refunded" tooltipText="Refunded" />
            </>
          )}
        </div>
      ) : null}

      {props?.data && props?.data?.length ? (
        props?.data.map((item: any, index: number) => {
          if (item.paymentStatus === PAYMENT_STATUS.PAID)
            ticketTotal += item.ticketPrice * item.selectedUnitsCount
          return (
            <DetailRow
              key={index}
              date={formatDateWithTime(item?.created)}
              item={item}
              showRefund={showRefund}
              currency={currency}
            />
          )
        })
      ) : (
        <span className="w-full text-sm text-SeabiscuitLightParagraphTextColor text-center">
          No tickets selected
        </span>
      )}

      {props?.data && props?.data?.length ? (
        <TotalRow
          modalThemeColor={props.modalThemeColor}
          numbersOfUnit={
            Array.isArray(props?.data)
              ? props?.data.reduce((accumulator, currentValue) => {
                  return +accumulator + +currentValue.selectedUnitsCount
                }, 0)
              : 0
          }
          price={new ModelBaseModel().getNum(ticketTotal).toString()}
          scratched={0}
          refunded={0}
          showRefund={showRefund}
          refundgovernanceTotalState={refundgovernanceTotalState}
          currency={currency}
        />
      ) : null}
    </>
  )
}

export default BillDetailsTicketsData
